import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Chakra } from "./Chakra";

const NAV = [
    { id: "mission", label: "Mission" },
    { id: "work", label: "What We Create" },
    { id: "participate", label: "Participate" },
    { id: "vision", label: "Vision" },
    { id: "careers", label: "Careers" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const headerOffset = 90;
        const rect = el.getBoundingClientRect();
        const targetY = rect.bottom + window.scrollY - window.innerHeight + 40;
        const top =
            id === "contribute-cta"
                ? Math.max(targetY, rect.top + window.scrollY - headerOffset)
                : rect.top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: "smooth" });
        setOpen(false);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 text-tip-black ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-black/10"
                    : "bg-transparent"
            }`}
        >
            <div className="tip-container flex items-center justify-between h-16 md:h-20">
                <button
                    data-testid="brand-logo"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="chakra-trigger flex items-center gap-[2px] group"
                >
                    <span className="font-display font-black text-xl md:text-2xl tracking-tight leading-none">
                        THE INDIA PROJEKT
                    </span>
                    <Chakra
                        size={18}
                        strokeWidth={1.4}
                        className="chakra-icon -translate-x-[1px]"
                    />
                </button>

                <nav className="hidden md:flex items-center gap-9">
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            data-testid={`nav-${n.id}`}
                            onClick={() => goTo(n.id)}
                            className="text-[11px] font-bold tracking-[0.22em] uppercase link-underline"
                        >
                            {n.label}
                        </button>
                    ))}
                </nav>

                <button
                    data-testid="nav-apply-cta"
                    onClick={() => goTo("contribute-cta")}
                    className="hidden md:inline-flex btn-block btn-block-ghost border-current"
                >
                    Contribute
                </button>

                <button
                    data-testid="mobile-menu-toggle"
                    className="md:hidden p-2"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="md:hidden bg-tip-black text-tip-white"
                    >
                        <div className="tip-container py-6 flex flex-col gap-5">
                            {NAV.map((n) => (
                                <button
                                    key={n.id}
                                    data-testid={`mobile-nav-${n.id}`}
                                    onClick={() => goTo(n.id)}
                                    className="text-left font-display text-2xl font-bold"
                                >
                                    {n.label}.
                                </button>
                            ))}
                            <button
                                data-testid="mobile-apply-cta"
                                onClick={() => goTo("contribute-cta")}
                                className="btn-block btn-block-light mt-4 self-start"
                            >
                                Contribute
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
